import { Divider } from "@portal/BaseComponents/Divider/AntDivider";
import { UploadedFiles } from "@portal/BaseComponents/Document/UplodedFiles/UploadedFiles";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Profile } from "@portal/BaseComponents/Profile/Profile";
import { InvoicesColumns } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesColumn";
import { mapInvoicesColumnPredicate } from "@portal/BaseComponents/Table/Components/Invoices/predicate";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { DisputeDetails } from "@portal/types/entities/dispute";

import { DetailsViewLayout } from "@portal/BaseComponents/DetailsView/DetailsView";
import {
  Skeleton,
  SkeletonInput,
} from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { InvoicesText } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesText";
import { DescriptionsProps } from "antd";
import { filter } from "lodash";
import { useMemo } from "react";
import { DetailsContent } from "../../../../DetailsView/Content/DetailsContent";

interface DisputeDetailsCompProps {
  data?: DisputeDetails;
  loading: boolean;
}
function DisputeDetailsComp(props: DisputeDetailsCompProps) {
  const { data, loading } = props;
  function getInvoices(): InvoicesColumns[] {
    if (!data) return [];
    return data.invoices.map(mapInvoicesColumnPredicate);
  }

  const invoices = useMemo(getInvoices, [data]);
  const Title = (
    <DetailsContent
      hideHeading
      wrapperStyle={{ paddingInlineStart: "43px" }}
      content={
        loading ? (
          <SkeletonInput />
        ) : (
          <TypographyText strong fs={"16"}>
            {data?.title}
          </TypographyText>
        )
      }
    />
  );

  const CreatedBy = (
    <Skeleton loading={loading} active avatar paragraph={{ rows: 0 }}>
      <Profile
        dateAndTime={data?.created_at}
        firstName={data?.created_by.first_name ?? ""}
        lastName={data?.created_by.last_name}
        designation={data?.created_by.designation}
        textStyle={{
          date: {
            fs: "12",
          },
        }}
      />
    </Skeleton>
  );
  const Desc = loading ? (
    <SkeletonInput />
  ) : (
    <TypographyText>{data?.description ?? "-"}</TypographyText>
  );

  const AssociatedInvoice = data ? (
    <Flex vertical space={"12"}>
      {invoices.map((invoice) => {
        return (
          <InvoicesText
            overRideStyle={{ width: "auto" }}
            invoice={invoice}
            key={invoice.id}
            hide={{ amount: false }}
            textStyle={{ amount: { fs: "14" }, title: { fs: "14" } }}
          />
        );
      })}
    </Flex>
  ) : (
    <SkeletonInput active />
  );

  const LineItem = data ? (
    <Flex space={"2"} vertical>
      {data.invoice_line_items.length
        ? data.invoice_line_items.map((f, index) => {
            return (
              <TypographyText style={{ maxWidth: "190px" }} key={f.id}>
                {`${index + 1}. ${f.product_name}`}
              </TypographyText>
            );
          })
        : "-"}
    </Flex>
  ) : (
    <SkeletonInput active />
  );
  const DisputeOwners = data ? (
    <Flex vertical space={"12"}>
      {data.participants.length
        ? data.participants.map((owner) => {
            return (
              <Profile
                key={owner.id}
                firstName={owner.first_name}
                lastName={owner.last_name}
                designation={owner.designation}
                wrapperStyle={{ align: "center" }}
              />
            );
          })
        : "-"}
    </Flex>
  ) : (
    <SkeletonInput active />
  );

  const Amount = data ? (
    <AmountText amount={data.amount.value} currency={data.amount.currency} />
  ) : (
    <SkeletonInput active />
  );
  const TopArea = (
    <Flex vertical space="8">
      {CreatedBy}
      {Title}
    </Flex>
  );
  const Attachments = (
    <>
      {loading ? (
        <SkeletonInput />
      ) : (
        <Flex wrap>
          {data?.attachments
            ? data?.attachments.map((file) => {
                return (
                  <UploadedFiles
                    file={file}
                    key={file.id}
                    closable={false}
                    previewType="EXPIRE_LINK"
                  />
                );
              })
            : "-"}
        </Flex>
      )}
    </>
  );

  function getItems(): DescriptionsProps["items"] {
    return [
      { key: "DESC", children: Desc, span: 2 },
      { key: "ATTACHMENTS", children: Attachments, span: 2 },
      {
        key: "INVOICES",
        children: AssociatedInvoice,
        label: "Associated Invoices",
      },
      { key: "LINE_ITEMS", children: LineItem, label: "Line Items" },
      {
        key: "DISPUTE_OWNERS",
        children: DisputeOwners,
        label: "Dispute Owners",
      },
      { key: "AMOUNT", children: Amount, label: "Amount" },
    ];
  }

  const items = getItems();

  const modified = filter(items, (list) => {
    if (!data?.attachments.length) {
      return list.key !== "ATTACHMENTS";
    }

    return true;
  });
  return (
    <Flex vertical space="24">
      <Flex vertical space={"16"} style={{ width: "100%" }}>
        {TopArea}
        <div style={{ width: "100%", paddingInlineStart: "43px" }}>
          <Divider style={{ margin: 0 }} />
        </div>
      </Flex>
      <DetailsViewLayout
        items={modified}
        style={{ paddingInlineStart: "48px" }}
      />
    </Flex>
  );
}

export { DisputeDetailsComp };
